import {
  GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signInWithRedirect,
  getRedirectResult, signInWithEmailAndPassword, signOut, type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { auth } from "./firebase";
import { esquecerEsteAparelho } from "./push";

/**
 * Sessão.
 *
 * `loading` existe para separar "ainda não sei" de "não tem ninguém". Sem
 * essa distinção, o app pisca a tela de login por um instante em toda
 * abertura — o Firebase leva alguns milissegundos para restaurar a sessão
 * do IndexedDB, e nesse intervalo `currentUser` é null.
 */
export interface Session {
  user: User | null;
  loading: boolean;
}

const SessionContext = createContext<Session>({ user: null, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Session>({ user: null, loading: true });

  useEffect(() => {
    // Se voltamos de um redirect de login, resolve antes de escutar.
    getRedirectResult(auth).catch(() => null);
    return onAuthStateChanged(
      auth,
      (user) => setState({ user, loading: false }),
      () => setState({ user: null, loading: false }),
    );
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export const useSession = (): Session => useContext(SessionContext);

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

/**
 * Entrar com Google.
 *
 * Popup primeiro porque não perde o estado da página. Em navegador que
 * bloqueia popup — Safari no iOS com frequência —, cai para redirect, que
 * sempre funciona mas recarrega o app.
 */
export async function signIn(): Promise<void> {
  try {
    await signInWithPopup(auth, provider);
  } catch (err) {
    const code = (err as { code?: string })?.code ?? "";
    const popupFalhou = [
      "auth/popup-blocked",
      "auth/popup-closed-by-user",
      "auth/cancelled-popup-request",
      "auth/operation-not-supported-in-this-environment",
    ].includes(code);
    if (popupFalhou) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw err;
  }
}

/**
 * Entrar com e-mail e senha.
 *
 * Existe porque a revisão da Google Play precisa entrar no app, e quem
 * revisa não pode criar conta nem usar a própria. Só o Google como porta
 * deixaria o formulário "Detalhes do login" sem credencial para dar — e
 * app que a revisão não consegue abrir é app rejeitado.
 *
 * Não há cadastro por aqui de propósito: as contas de e-mail nascem à
 * mão, no console do Firebase. Uma tela de cadastro seria superfície
 * nova (confirmação de e-mail, recuperação de senha, abuso) para
 * resolver um problema que é de uma conta só.
 */
export async function signInComEmail(email: string, senha: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email.trim(), senha);
}

export async function signOutNow(): Promise<void> {
  // Antes do signOut: depois dele a regra já não deixa apagar o documento.
  const uid = auth.currentUser?.uid;
  if (uid) await esquecerEsteAparelho(uid);
  await signOut(auth);
}

/**
 * O nome que vale para esta sessão.
 *
 * Conta do Google chega com `displayName` preenchido; conta de e-mail e
 * senha nasce sem nenhum. Sem isto, quem entra por e-mail aparece como
 * "Sem nome" no grupo, no prato e no placar — e o pedaço do e-mail antes
 * do @ é um palpite melhor que "Sem nome" para todo mundo que olha.
 *
 * É só o ponto de partida: o apelido escolhido em Conta vence este e não
 * volta atrás, porque o `ensureUser` só sobrescreve o nome de quem nunca
 * escolheu um (a marca `nickname`).
 */
export function nomeDaSessao(user: User | null): string {
  const doProvedor = user?.displayName?.trim();
  if (doProvedor) return doProvedor;
  const antesDoArroba = (user?.email ?? "").split("@")[0]?.trim();
  return antesDoArroba || "Sem nome";
}

/** Mensagem que serve para a pessoa, não para o log. */
export function authErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  if (code === "auth/unauthorized-domain") {
    return "Este endereço não está autorizado no Firebase Auth.";
  }
  if (code === "auth/network-request-failed") {
    return "Sem conexão. Tenta de novo daqui a pouco.";
  }
  /* A Auth responde `invalid-credential` para senha errada E para e-mail
     que não existe, de propósito: dizer qual dos dois errou entrega quem
     tem conta no app. A mensagem aqui não desfaz isso. */
  if (["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found"].includes(code)) {
    return "E-mail ou senha não conferem.";
  }
  if (code === "auth/invalid-email") return "Esse e-mail não parece um e-mail.";
  if (code === "auth/user-disabled") return "Esta conta está desativada.";
  if (code === "auth/too-many-requests") {
    return "Muita tentativa seguida. Espera alguns minutos e tenta de novo.";
  }
  return "Não deu pra entrar. Tenta de novo.";
}
